import React, { createContext, useContext, useState, useCallback, useMemo, useEffect, useRef } from 'react';
import { useAuth } from './AuthContext';
import { logContentChange, logResetAll, logAdminRevert, markActivityReverted } from '../services/activityLog';
import {
  clearSiteContent,
  fetchSiteContent,
  migrateLocalContentToSupabase,
  saveSiteContent,
} from '../services/siteContent';

const ContentContext = createContext(null);
const AUTO_SAVE_DELAY_MS = 1500;

function cloneContent(value) {
  return JSON.parse(JSON.stringify(value));
}

function contentEquals(a, b) {
  return JSON.stringify(a) === JSON.stringify(b);
}

function logDraftChanges(previous, next, page) {
  const keys = new Set([...Object.keys(previous), ...Object.keys(next)]);

  keys.forEach((key) => {
    const oldValue = previous[key];
    const newValue = next[key];

    if (contentEquals(oldValue, newValue)) return;

    logContentChange({
      key,
      oldValue,
      newValue,
      page,
    });
  });
}

export function ContentProvider({ children }) {
  const { isEditing } = useAuth();
  const [savedContent, setSavedContent] = useState({});
  const [draftContent, setDraftContent] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState('');

  const savedContentRef = useRef(savedContent);
  const draftContentRef = useRef(draftContent);
  const saveInFlightRef = useRef(false);
  const saveQueuedRef = useRef(false);
  const autoSaveTimerRef = useRef(null);

  savedContentRef.current = savedContent;
  draftContentRef.current = draftContent;

  const activeContent = isEditing ? draftContent : savedContent;

  useEffect(() => {
    let cancelled = false;

    async function loadContent() {
      setIsLoading(true);
      await migrateLocalContentToSupabase();
      const content = await fetchSiteContent();

      if (!cancelled) {
        setSavedContent(content);
        setDraftContent(cloneContent(content));
        setIsLoading(false);
      }
    }

    loadContent();

    return () => {
      cancelled = true;
    };
  }, []);

  const wasEditingRef = useRef(false);

  useEffect(() => {
    if (isEditing && !wasEditingRef.current) {
      setDraftContent(cloneContent(savedContent));
      setSaveError('');
    }
    wasEditingRef.current = isEditing;
  }, [isEditing, savedContent]);

  useEffect(() => {
    if (!isEditing || isLoading) return;
    setDraftContent((prev) => {
      if (Object.keys(prev).length > 0) return prev;
      return cloneContent(savedContent);
    });
  }, [isEditing, isLoading, savedContent]);

  const hasUnsavedChanges = useMemo(
    () => isEditing && !contentEquals(savedContent, draftContent),
    [isEditing, savedContent, draftContent]
  );

  useEffect(() => {
    if (!hasUnsavedChanges) return undefined;

    const handleBeforeUnload = (event) => {
      event.preventDefault();
      event.returnValue = '';
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [hasUnsavedChanges]);

  const getContent = useCallback(
    (key, fallback = '') => {
      if (Object.prototype.hasOwnProperty.call(activeContent, key)) {
        const value = activeContent[key];
        if (value === undefined || value === null) return fallback;
        if (typeof value === 'string' && value.trim() === '') return fallback;
        return value;
      }
      return fallback;
    },
    [activeContent]
  );

  const setContentValue = useCallback((key, value) => {
    if (!isEditing) return;

    setDraftContent((prev) => {
      if (prev[key] === value) return prev;
      return { ...prev, [key]: value };
    });
  }, [isEditing]);

  const clearContentValue = useCallback((key) => {
    if (!isEditing) return;

    setDraftContent((prev) => {
      if (!Object.prototype.hasOwnProperty.call(prev, key)) {
        return prev;
      }

      const next = { ...prev };
      delete next[key];
      return next;
    });
  }, [isEditing]);

  const persistToSupabase = useCallback(async () => {
    if (!isEditing) {
      return { saved: false };
    }

    const previous = savedContentRef.current;
    const next = draftContentRef.current;

    if (contentEquals(previous, next)) {
      return { saved: false };
    }

    if (saveInFlightRef.current) {
      saveQueuedRef.current = true;
      return { saved: false, queued: true };
    }

    saveInFlightRef.current = true;
    setIsSaving(true);

    try {
      const result = await saveSiteContent(next, previous);

      if (!result.ok) {
        const message = 'Could not save changes to Supabase. Please try again.';
        setSaveError(message);
        return { saved: false, error: message };
      }

      logDraftChanges(previous, next, window.location.pathname);
      setSavedContent(cloneContent(next));
      setSaveError('');
      return { saved: true };
    } finally {
      saveInFlightRef.current = false;
      setIsSaving(false);

      if (saveQueuedRef.current) {
        saveQueuedRef.current = false;
        persistToSupabase();
      }
    }
  }, [isEditing]);

  useEffect(() => {
    if (!isEditing || isLoading || !hasUnsavedChanges) {
      return undefined;
    }

    if (autoSaveTimerRef.current) {
      clearTimeout(autoSaveTimerRef.current);
    }

    autoSaveTimerRef.current = window.setTimeout(() => {
      autoSaveTimerRef.current = null;
      persistToSupabase();
    }, AUTO_SAVE_DELAY_MS);

    return () => {
      if (autoSaveTimerRef.current) {
        clearTimeout(autoSaveTimerRef.current);
        autoSaveTimerRef.current = null;
      }
    };
  }, [draftContent, isEditing, isLoading, hasUnsavedChanges, persistToSupabase]);

  const saveChanges = useCallback(async () => {
    if (autoSaveTimerRef.current) {
      clearTimeout(autoSaveTimerRef.current);
      autoSaveTimerRef.current = null;
    }

    return persistToSupabase();
  }, [persistToSupabase]);

  const discardChanges = useCallback(() => {
    if (autoSaveTimerRef.current) {
      clearTimeout(autoSaveTimerRef.current);
      autoSaveTimerRef.current = null;
    }

    setDraftContent(cloneContent(savedContent));
    setSaveError('');
  }, [savedContent]);

  const confirmDiscardUnsavedChanges = useCallback(() => {
    if (!hasUnsavedChanges) return true;

    const shouldDiscard = window.confirm(
      'You have unsaved changes. Discard them and continue?'
    );

    if (shouldDiscard) {
      discardChanges();
    }

    return shouldDiscard;
  }, [hasUnsavedChanges, discardChanges]);

  const resetContent = useCallback(async () => {
    if (autoSaveTimerRef.current) {
      clearTimeout(autoSaveTimerRef.current);
      autoSaveTimerRef.current = null;
    }

    logResetAll({ page: window.location.pathname });
    await clearSiteContent();
    setSavedContent({});
    setDraftContent({});
    setSaveError('');
  }, []);

  const revertContentChange = useCallback(async ({ contentKey, oldValue, entryId, entryLabel }) => {
    if (!isEditing) {
      return { ok: false, error: 'Sign in to revert changes.' };
    }

    const previous = cloneContent(savedContentRef.current);
    const next = cloneContent(draftContentRef.current);

    if (oldValue === undefined) {
      delete next[contentKey];
    } else {
      next[contentKey] = cloneContent(oldValue);
    }

    setDraftContent(next);
    setIsSaving(true);

    try {
      const result = await saveSiteContent(next, previous);
      if (!result.ok) {
        setSaveError('Could not revert this change. Please try again.');
        return { ok: false, error: 'Could not revert this change.' };
      }

      setSavedContent(cloneContent(next));
      setSaveError('');

      if (entryId) {
        await markActivityReverted(entryId);
      }

      logAdminRevert({
        contentKey,
        label: entryLabel,
        summary: `Reverted "${entryLabel || contentKey}" to its previous value`,
        page: window.location.pathname,
      });

      return { ok: true };
    } finally {
      setIsSaving(false);
    }
  }, [isEditing]);

  return (
    <ContentContext.Provider
      value={{
        getContent,
        setContentValue,
        clearContentValue,
        saveChanges,
        discardChanges,
        confirmDiscardUnsavedChanges,
        hasUnsavedChanges,
        isLoading,
        isSaving,
        saveError,
        resetContent,
        revertContentChange,
      }}
    >
      {children}
    </ContentContext.Provider>
  );
}

export function useContent() {
  const context = useContext(ContentContext);
  if (!context) {
    throw new Error('useContent must be used within ContentProvider');
  }
  return context;
}
